import { onAnimationEnd, isMobileBreakpoint } from '@theme/utilities';

// Create a new custom element for product links with images for transitions to PDP
class ProductCardLink extends HTMLElement {
  /** @type {{ image: HTMLImageElement, srcset: string } | null} */
  #mutatedImage = null;

  connectedCallback() {
    this.addEventListener('click', (event) => setTimeout(() => this.#handleClick(event), 0));
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleClick = (event) => {
    // If the event has been prevented, don't do anything, another component is handling the click or if it's an input (swatches)
    if (event.defaultPrevented || event.target instanceof HTMLInputElement) return;

    // Skip the image-swap transition on mobile. Page navigation there is slow enough, relative to
    // the swap, that visitors end up briefly seeing the product's real featured image in place of
    // the card's custom image before the page actually changes.
    if (isMobileBreakpoint()) return;

    const gallery = this.querySelector('[data-view-transition-to-main-product]');
    if (!this.productTransitionEnabled || !(gallery instanceof HTMLElement)) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const activeImage =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]') ||
      gallery.querySelector('[transitionToProduct="true"]:last-child');

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    gallery.setAttribute('data-view-transition-type', 'product-image-transition');
    gallery.setAttribute('data-view-transition-triggered', 'true');
  };

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (this.featuredMediaUrl && !image.srcset.includes(this.featuredMediaUrl)) {
      this.#mutatedImage = { image, srcset: image.srcset };

      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }

  /**
   * The srcset swap in #setImageSrcset is only meant to make the view transition to the PDP look seamless.
   * When the browser restores this page from the back/forward cache (eg. after pressing the browser's back
   * button), the DOM mutation persists, so the card would otherwise be left showing the product's actual
   * featured image instead of the custom image configured in the page builder. Restore it here.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted || !this.#mutatedImage) return;

    const { image, srcset } = this.#mutatedImage;
    image.srcset = srcset;
    this.#mutatedImage = null;
  };
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}
